import { ClearRounded, DeleteOutlineRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { GlobalState } from "context/GlobalContext";
import { useMemo, useState } from "react";
import { BQ_MASTER_CACHE_KEYS } from "../../shared/bqMasterDropdownCache";
import { itemDescriptionFromMaster, parseItemRate, poMasterFetches } from "../data";

const FIELD_ALIASES = {
	barcode: ["barcode", "bar_code", "ean", "upc"],
	qty: ["qty", "quantity", "ordqty", "qnty"],
	rate: ["rate", "price", "unit_rate", "unit_price"],
	remarks: ["remarks", "remark", "notes", "comment"],
};

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatCellValue = (value) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const pad = (item) => String(item).padStart(2, "0");
		return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
	}
	if (typeof value === "object") {
		if (value?.text != null) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
		if (value?.richText?.length)
			return value.richText
				.map((part) => part.text)
				.join("")
				.trim();
	}
	return String(value).trim();
};

const normalizeKeyValue = (value) => String(value ?? "").trim();

const itemMatchesBarcode = (row, barcode) => {
	const b = String(barcode);
	return String(row?.barcode ?? "") === b || String(row?.key ?? "") === b;
};

const resolveBarcodeRow = async (barcode, getDropdownCache) => {
	const code = String(barcode || "").trim();
	if (!code) return null;

	const cached = getDropdownCache?.(BQ_MASTER_CACHE_KEYS.itemsPo) || [];
	const fromCache = cached.find((row) => itemMatchesBarcode(row, code));
	if (fromCache?.item_code) {
		return {
			barcode: fromCache.barcode || code,
			item_code: String(fromCache.item_code),
			item_description: fromCache.item_description || itemDescriptionFromMaster(fromCache) || "",
			rate: parseItemRate(fromCache.rate ?? fromCache.cost),
		};
	}

	try {
		const res = await poMasterFetches.barcode(0, 20, code);
		const rows = res?.data?.result || [];
		const exact = rows.find((row) => itemMatchesBarcode(row, code)) || rows[0];
		if (!exact?.item_code) return null;
		return {
			barcode: exact.barcode || code,
			item_code: String(exact.item_code),
			item_description: exact.item_description || itemDescriptionFromMaster(exact) || "",
			rate: parseItemRate(exact.rate),
		};
	} catch {
		return null;
	}
};

const PoLineItemsUploadModal = ({ modal, setModal, existingBarcodes = [], onConfirm }) => {
	const { getDropdownCache } = GlobalState();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [barcodeColumnIndex, setBarcodeColumnIndex] = useState("");
	const [qtyColumnIndex, setQtyColumnIndex] = useState("");
	const [rateColumnIndex, setRateColumnIndex] = useState("");
	const [remarksColumnIndex, setRemarksColumnIndex] = useState("");
	const [previewRows, setPreviewRows] = useState([]);

	const existingSet = useMemo(
		() => new Set((existingBarcodes || []).map((b) => normalizeKeyValue(b)).filter(Boolean)),
		[existingBarcodes]
	);

	const resetState = () => {
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setBarcodeColumnIndex("");
		setQtyColumnIndex("");
		setRateColumnIndex("");
		setRemarksColumnIndex("");
		setPreviewRows([]);
	};

	const handleClose = () => {
		resetState();
		setModal(false);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return { headers: [], rows: [] };

		const parsedHeaders = parseCsvLine(lines[0]).map((value, index) => value || `Column ${index + 1}`);
		const parsedRows = lines.slice(1).map((line) => parseCsvLine(line));
		return { headers: parsedHeaders, rows: parsedRows };
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(await file.arrayBuffer());
		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);

		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const getDefaultIndex = (list, targetKeys) =>
		list.findIndex((header) => targetKeys.includes(normalizeHeader(header)));

	const detectColumns = (headerList) => {
		const barcodeIdx = getDefaultIndex(headerList, FIELD_ALIASES.barcode);
		const qtyIdx = getDefaultIndex(headerList, FIELD_ALIASES.qty);
		const rateIdx = getDefaultIndex(headerList, FIELD_ALIASES.rate);
		const remarksIdx = getDefaultIndex(headerList, FIELD_ALIASES.remarks);
		return {
			barcodeIndex: barcodeIdx >= 0 ? String(barcodeIdx) : "",
			qtyIndex: qtyIdx >= 0 ? String(qtyIdx) : "",
			rateIndex: rateIdx >= 0 ? String(rateIdx) : "",
			remarksIndex: remarksIdx >= 0 ? String(remarksIdx) : "",
		};
	};

	const buildMappedRows = async ({
		rows = sourceRows,
		barcodeColumn = barcodeColumnIndex,
		qtyColumn = qtyColumnIndex,
		rateColumn = rateColumnIndex,
		remarksColumn = remarksColumnIndex,
	} = {}) => {
		if (barcodeColumn === "") {
			throw new Error("Select barcode column first.");
		}

		const barcodeIndex = Number(barcodeColumn);
		const qtyIndex = qtyColumn === "" ? -1 : Number(qtyColumn);
		const rateIndex = rateColumn === "" ? -1 : Number(rateColumn);
		const remarksIndex = remarksColumn === "" ? -1 : Number(remarksColumn);

		const grouped = new Map();

		rows.forEach((cells) => {
			const barcode = formatCellValue(cells?.[barcodeIndex]);
			if (!barcode) return;

			const qtyRaw = qtyIndex >= 0 ? formatCellValue(cells?.[qtyIndex]) : "";
			const rateRaw = rateIndex >= 0 ? formatCellValue(cells?.[rateIndex]) : "";
			const remarks = remarksIndex >= 0 ? formatCellValue(cells?.[remarksIndex]) : "";
			const qty = qtyRaw === "" ? 1 : Number(qtyRaw);
			const rate = rateRaw === "" ? null : parseItemRate(rateRaw);

			const key = normalizeKeyValue(barcode);
			const prev = grouped.get(key);
			if (!prev) {
				grouped.set(key, {
					barcode,
					qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
					rate: Number.isFinite(rate) ? rate : null,
					remarks,
					__count: 1,
				});
				return;
			}

			prev.__count += 1;
			prev.qty += Number.isFinite(qty) && qty > 0 ? qty : 1;
			if (prev.rate == null && Number.isFinite(rate)) prev.rate = rate;
			if (!prev.remarks && remarks) prev.remarks = remarks;
		});

		const mapped = [...grouped.values()];
		if (!mapped.length) {
			throw new Error("No barcode rows found in file.");
		}

		const resolved = [];
		for (let index = 0; index < mapped.length; index += 1) {
			const row = mapped[index];
			const key = normalizeKeyValue(row.barcode);
			const alreadyOnPo = existingSet.has(key);
			const hit = alreadyOnPo ? null : await resolveBarcodeRow(row.barcode, getDropdownCache);

			if (alreadyOnPo) {
				resolved.push({
					...row,
					item_code: "",
					__rowId: `po-upload-${index}-${key}`,
					__isValid: false,
					__status: "Duplicate",
					__note: "Barcode already on this PO",
				});
				continue;
			}

			if (!hit) {
				resolved.push({
					...row,
					item_code: "",
					excel_rate: row.rate,
					im_rate: null,
					__rowId: `po-upload-${index}-${key}`,
					__isValid: false,
					__status: "Missing",
					__note: "Barcode not found in INVITEM",
				});
				continue;
			}

			const excelRate = row.rate != null ? Number(row.rate) : null;
			const imRate = hit.rate != null ? Number(hit.rate) : null;
			const hasExcelRate = excelRate != null && Number.isFinite(excelRate);
			const hasImRate = imRate != null && Number.isFinite(imRate);
			const rateMismatch =
				hasExcelRate && hasImRate && Math.abs(excelRate - imRate) > 0.0001;

			const mergeNote = row.__count > 1 ? `Merged ${row.__count} rows with same barcode. ` : "";

			if (rateMismatch) {
				resolved.push({
					barcode: hit.barcode || row.barcode,
					item_description: hit.item_description || "",
					item_code: hit.item_code,
					qty: row.qty,
					rate: excelRate,
					excel_rate: excelRate,
					im_rate: imRate,
					remarks: row.remarks || "",
					__rowId: `po-upload-${index}-${key}`,
					__isValid: false,
					__status: "Rate Mismatch",
					__note: `${mergeNote}Excel rate ${excelRate} ≠ INVITEM COSTRATE ${imRate}`,
				});
				continue;
			}

			resolved.push({
				barcode: hit.barcode || row.barcode,
				item_description: hit.item_description || "",
				item_code: hit.item_code,
				qty: row.qty,
				rate: hasExcelRate ? excelRate : imRate,
				excel_rate: hasExcelRate ? excelRate : null,
				im_rate: imRate,
				remarks: row.remarks || "",
				__rowId: `po-upload-${index}-${key}`,
				__isValid: true,
				__status: "Valid",
				__note: mergeNote
					? `${mergeNote}Ready to upload`
					: hasExcelRate
						? "Ready to upload (rate matches INVITEM COSTRATE)"
						: "Ready to upload (rate from INVITEM COSTRATE)",
			});
		}

		return resolved;
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;

		setLoading(true);
		setError("");
		setPreviewRows([]);

		try {
			const isCsv = file.name.toLowerCase().endsWith(".csv");
			const parsed = isCsv ? await parseCsvFile(file) : await parseExcelFile(file);
			if (!parsed.headers.length) throw new Error("No headers found in file.");

			const detected = detectColumns(parsed.headers);
			setFileName(file.name);
			setHeaders(parsed.headers);
			setSourceRows(parsed.rows);
			setBarcodeColumnIndex(detected.barcodeIndex);
			setQtyColumnIndex(detected.qtyIndex);
			setRateColumnIndex(detected.rateIndex);
			setRemarksColumnIndex(detected.remarksIndex);

			if (detected.barcodeIndex !== "") {
				const nextRows = await buildMappedRows({
					rows: parsed.rows,
					barcodeColumn: detected.barcodeIndex,
					qtyColumn: detected.qtyIndex,
					rateColumn: detected.rateIndex,
					remarksColumn: detected.remarksIndex,
				});
				setPreviewRows(nextRows);
			}
		} catch (fileError) {
			setError(fileError.message || "Unable to read file.");
			setHeaders([]);
			setSourceRows([]);
			setPreviewRows([]);
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = async () => {
		setLoading(true);
		setError("");
		try {
			const nextRows = await buildMappedRows();
			setPreviewRows(nextRows);
		} catch (previewError) {
			setError(previewError.message || "Unable to preview file.");
			setPreviewRows([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("PO Items");
		worksheet.addRow(["barcode", "qty", "rate", "remarks"]);
		worksheet.addRow(["8901234567890", 10, 407, "sample"]);
		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "po_line_items_upload_format.xlsx";
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleDeleteRow = (rowId) => {
		setPreviewRows((prev) => prev.filter((row) => row.__rowId !== rowId));
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();
		if (typeof onConfirm !== "function") return;

		const validRows = previewRows
			.filter((row) => row.__isValid !== false)
			.map((row) => ({
				barcode: row.barcode,
				item_description: row.item_description || "",
				item_code: row.item_code,
				qty: row.qty,
				rate: row.rate,
				remarks: row.remarks || "",
			}));

		if (!validRows.length) {
			setError("No valid rows to upload.");
			return;
		}

		setLoading(true);
		try {
			const result = await onConfirm(validRows);
			if (result !== false) handleClose();
		} catch (submitError) {
			setError(submitError.message || "Upload failed.");
		} finally {
			setLoading(false);
		}
	};

	const validCount = previewRows.filter((row) => row.__isValid !== false).length;
	const previewGridKey = useMemo(
		() => previewRows.map((row) => `${row.__rowId}:${row.__status}:${row.__note}:${row.__isValid}`).join("||"),
		[previewRows]
	);

	const columnSelect = (label, value, onChange) =>
		headers.length > 0 ? (
			<FormControl size="small" sx={{ minWidth: 180 }}>
				<InputLabel>{label}</InputLabel>
				<Select label={label} value={value} onChange={(event) => onChange(event.target.value)} disabled={loading}>
					<MenuItem value="">
						<em>None</em>
					</MenuItem>
					{headers.map((header, index) => (
						<MenuItem key={`${header}-${index}`} value={String(index)}>
							{header}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		) : null;

	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "UPLOAD STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 120,
				maxWidth: 140,
				valueGetter: ({ row }) =>
					row.__isValid === false ? row.__status || "Invalid" : row.__status || "Valid",
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__isValid === false ? row.__status || "Invalid" : row.__status || "Valid"}
					</Box>
				),
			},
			{ key: "barcode", name: "BARCODE", headerAlign: "center", align: "center", minWidth: 150 },
			{
				key: "item_description",
				name: "ITEM DESCRIPTION",
				headerAlign: "center",
				align: "center",
				minWidth: 200,
				valueGetter: ({ row }) => row.item_description || "—",
			},
			{ key: "item_code", name: "ITEM_CODE", headerAlign: "center", align: "center", minWidth: 130 },
			{ key: "qty", name: "QTY", headerAlign: "center", align: "center", minWidth: 90 },
			{
				key: "excel_rate",
				name: "EXCEL RATE",
				headerAlign: "center",
				align: "center",
				minWidth: 110,
				valueGetter: ({ row }) => (row.excel_rate == null || row.excel_rate === "" ? "—" : row.excel_rate),
				renderCell: ({ row }) => (
					<Box
						fontWeight={row.__status === "Rate Mismatch" ? 700 : 400}
						color={row.__status === "Rate Mismatch" ? "error.main" : "inherit"}>
						{row.excel_rate == null || row.excel_rate === "" ? "—" : row.excel_rate}
					</Box>
				),
			},
			{
				key: "im_rate",
				name: "IM COST",
				headerAlign: "center",
				align: "center",
				minWidth: 100,
				valueGetter: ({ row }) => (row.im_rate == null || row.im_rate === "" ? "—" : row.im_rate),
			},
			{
				key: "remarks",
				name: "REMARKS",
				headerAlign: "center",
				align: "center",
				minWidth: 140,
				valueGetter: ({ row }) => row.remarks || "—",
			},
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 220,
				renderCell: ({ row }) => (
					<Typography
						variant="body2"
						color={row.__isValid === false ? "error.main" : "text.secondary"}
						sx={{ width: "100%" }}
						textAlign="center">
						{row.__note || ""}
					</Typography>
				),
			},
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				maxWidth: 90,
				valueGetter: () => "",
				renderCell: ({ row }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(event) => {
							event.stopPropagation();
							handleDeleteRow(row.__rowId);
						}}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		],
		[]
	);

	return (
		<Dialog
			maxWidth="xl"
			fullWidth
			scroll="paper"
			open={modal}
			onClose={handleClose}
			PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload PO Line Items</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
							Upload CSV/Excel with a <strong>barcode</strong> column. Optionally map <strong>qty</strong>,{" "}
							<strong>rate</strong>, and <strong>remarks</strong>. Excel <strong>rate</strong> is compared to
							INVITEM <strong>COSTRATE</strong> — mismatches are flagged and blocked from Save Upload.
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<Button
								component="label"
								variant="contained"
								color="primary"
								disableElevation
								size="small"
								sx={{ minWidth: 150, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								onClick={handleDownloadFormat}
								disabled={loading}
								size="small"
								startIcon={<FileDownloadRounded />}
								sx={{
									minWidth: 138,
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									borderStyle: "dashed",
									py: 0.9,
								}}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								disableElevation
								size="small"
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
								onClick={handlePreview}
								disabled={loading || !headers.length || barcodeColumnIndex === ""}
								sx={{ minWidth: 132, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} — ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", columnGap: 2, flexWrap: "wrap", rowGap: 1, pt: 1 }}>
						{columnSelect("barcode Column", barcodeColumnIndex, setBarcodeColumnIndex)}
						{columnSelect("qty Column", qtyColumnIndex, setQtyColumnIndex)}
						{columnSelect("rate Column", rateColumnIndex, setRateColumnIndex)}
						{columnSelect("remarks Column", remarksColumnIndex, setRemarksColumnIndex)}
					</Box>

					{error && <Alert severity="error">{error}</Alert>}

					<Stack sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
						{previewRows.length > 0 && (
							<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
								<TanstackDatagrid
									key={previewGridKey}
									columns={uploadPreviewColumns}
									pageData={previewRows}
									setPageData={setPreviewRows}
									getRowId={(row) => row.__rowId}
									disablePagination
									serverSidePagination={false}
									enableHorizontalScroll
									headerHeight={40}
									rowHeight={35}
									processRowUpdate={(newRow) => newRow}
									isCellEditable={({ column }) =>
										![
											"__upload_status",
											"__note",
											"__delete",
											"item_description",
											"item_code",
											"excel_rate",
											"im_rate",
										].includes(column.key)
									}
									children={
										<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
											<Typography variant="body2" color="text.secondary">
												{`${validCount} valid barcode${validCount === 1 ? "" : "s"} can be uploaded from ${sourceRows.length} rows`}
											</Typography>
										</Box>
									}
								/>
							</Box>
						)}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || validCount === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PoLineItemsUploadModal;
